import React from 'react';
import {
  LuPencil,
  LuCopy,
  LuArchive,
  LuTrash2,
  LuRotateCcw,
  LuGitBranch,
  LuRadar,
  LuLink,
} from 'react-icons/lu';
import { hasRepositoryLinked } from './projectHealth';

function integrationSource(project) {
  const raw = project?.originalData?.integration_source;
  if (raw === null || raw === undefined || raw === '') return null;
  const numeric = Number(raw);
  return Number.isNaN(numeric) ? raw : numeric;
}

/**
 * Overflow actions for a project card or row.
 * Skips screens that already live in the project sidebar after Open Project.
 */
export function buildProjectMenuItems(project, {
  bitbucketConfigured = false,
  onEdit,
  onIntegration,
  onLinkRepo,
  onCrawl,
  onDuplicate,
  onArchive,
  onUnarchive,
  onActivate,
  onDelete,
} = {}) {
  const status = String(project?.status || '').toLowerCase();

  if (status === 'archived') {
    return [
      {
        id: 'unarchive',
        label: 'Activate',
        description: 'Make this project active again',
        icon: <LuRotateCcw size={15} />,
        onClick: () => onUnarchive?.(project),
      },
    ];
  }

  if (status === 'inactive') {
    return [
      {
        id: 'activate',
        label: 'Activate project',
        description: 'Make this project active again',
        icon: <LuRotateCcw size={15} />,
        onClick: () => onActivate?.(project),
      },
    ];
  }

  const repoLinked = hasRepositoryLinked(project);
  const items = [];

  if (onEdit) {
    items.push({
      id: 'edit',
      group: 'Manage',
      label: 'Edit details',
      description: 'Name, type, and description',
      icon: <LuPencil size={15} />,
      onClick: () => onEdit(project),
    });
  }

  if (onIntegration && integrationSource(project) === null) {
    items.push({
      id: 'link-project',
      group: 'Manage',
      label: 'Link Project',
      description: 'Connect Jira or WorkXiq',
      icon: <LuLink size={15} />,
      onClick: () => onIntegration(project),
    });
  }

  if (onLinkRepo) {
    items.push({
      id: 'repo',
      group: 'Manage',
      label: repoLinked ? 'Update repository' : 'Link repository',
      description: bitbucketConfigured
        ? 'Connect Bitbucket repo for automation scripts'
        : 'Connect Bitbucket in Settings first',
      icon: <LuGitBranch size={15} />,
      disabled: !bitbucketConfigured,
      onClick: () => onLinkRepo(project),
    });
  }

  if (onCrawl) {
    items.push({
      id: 'crawl',
      group: 'Manage',
      label: 'Crawl Project',
      description: 'Discover pages and flows of the application',
      icon: <LuRadar size={15} />,
      onClick: () => onCrawl(project),
    });
  }

  items.push(
    {
      id: 'duplicate',
      group: 'Organize',
      label: 'Duplicate',
      description: 'Create a copy of this project',
      icon: <LuCopy size={15} />,
      dividerBefore: true,
      onClick: () => onDuplicate?.(project),
    },
    {
      id: 'archive',
      group: 'Organize',
      label: 'Archive',
      description: 'Hide from active projects',
      icon: <LuArchive size={15} />,
      onClick: () => onArchive?.(project),
    }
  );

  if (status === 'active' && onDelete) {
    items.push({
      id: 'delete',
      label: 'Delete',
      description: 'Permanently remove this project',
      icon: <LuTrash2 size={15} />,
      danger: true,
      dividerBefore: true,
      onClick: () => onDelete(project),
    });
  }

  return items;
}
